import { getUserDemoEmailFromLocalStorage, isDemo } from '@features/demo/store';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import {
  getLocalTimestamp,
  getSignupSource,
  identifyMixpanelUser,
  resetMixpanelUser,
  trackMixpanelEvent,
  trackMixpanelLifecycleEvent,
  trackMixpanelOnce,
} from './mixpanel';
import { OnboardingStepKey } from '@features/onboarding/guide-visibility';
import { useAuthContext } from '@shared/auth/AuthContext';

export type SubscriptionTier = 'pro' | 'expert';

export type AnalyticsEvents =
  | { name: 'demo_started'; props: { source?: string } }
  | { name: 'demo_email_submitted'; props: { email: string } }
  | { name: 'demo_requested'; props: { source: string } }
  | {
      name: 'onboarding_step_completed';
      props: {
        step_key: OnboardingStepKey;
        step_number?: number;
      };
    }
  | { name: 'onboarding_fully_completed'; props: { total_steps: number } }

  // MONETIZACE & RETENCE
  | {
      name: 'subscription_changed';
      props: { from_tier: SubscriptionTier; to_tier: SubscriptionTier };
    }
  | { name: 'subscription_renewed'; props: { plan_tier: SubscriptionTier; price_czk: number } }
  | { name: 'subscription_cancelled'; props: { plan_tier: SubscriptionTier; reason?: string } }

  //KLÍČOVÉ FUNKCE
  | { name: 'ai_stock_intake_completed'; props: { items_count: number; stock_id?: string } }
  | { name: 'visit_photo_uploaded'; props: { has_description: boolean } }
  | { name: 'qr_payment_generated'; props: { payment_type: string } }
  | {
      name: 'pricing_calculator_used';
      props: {
        costs_count: number;
        has_costs: boolean;
        calculation_month?: string;
        costs_total?: number;
      };
    }
  | { name: 'consumption_viewed'; props: { filter_type?: string; has_filters: boolean } }
  | { name: 'online_reservation_received'; props: { action: 'approved' | 'denied' } }
  | { name: 'sms_template_saved'; props: { template_type: string; is_update: boolean } }
  | { name: 'color_scheme_changed'; props: { color: string } }
  | { name: 'import_clients'; props: { clients_count: number } }
  | {
      name: 'service_durations_configured';
      props: { services_edited_count: number; duration_source: string };
    }
  | { name: 'loyalty_program_used'; props: { action: string } }
  | { name: 'tutorial_opened'; props: { tutorial_count?: number } }
  | { name: 'search_used'; props: { search_type: string; has_query: boolean } }
  | { name: 'search_filter_used'; props: { search_type: string; filter: string } };

// dodat do reportu
// service_durations_configured
// import_clients
type AnalyticsProperties = Record<string, string | number | boolean | undefined>;

type TrackOptions = {
  once?: boolean;
  userId?: string;
  dedupeKey?: string;
};

export const useAnalytics = () => {
  const { isDemo, user } = useAuthContext();

  const trackEvent = useCallback(
    <T extends AnalyticsEvents>(
      eventName: T['name'],
      properties: T['props'],
      options?: TrackOptions,
    ) => {
      const payload = {
        ...properties,
        timestamp_local: getLocalTimestamp(),

        is_demo: isDemo,
        user_id: user?.sub ?? getUserDemoEmailFromLocalStorage() ?? 'anonymous',
        ...(isDemo ? { user_demo_email: getUserDemoEmailFromLocalStorage() } : {}),
      } as AnalyticsProperties;

      if (options?.once) {
        trackMixpanelOnce(eventName, payload, options.dedupeKey);
        return;
      }

      if (options?.userId) {
        trackMixpanelLifecycleEvent(eventName, options.userId, payload);
        return;
      }

      trackMixpanelEvent(eventName, payload);
    },
    [user?.sub],
  );

  const identifyUser = useCallback((userId: string, userTraits?: AnalyticsProperties) => {
    identifyMixpanelUser(userId, userTraits);
  }, []);

  const trackOnboardingStep = useCallback(
    (stepKey: OnboardingStepKey, stepNumber?: number) => {
      if (!isDemo) {
        trackEvent(
          'onboarding_step_completed',
          { step_key: stepKey, step_number: stepNumber },
          { once: true, dedupeKey: `onboarding_step_completed:${stepKey}` },
        );
      }
    },
    [trackEvent],
  );

  return useMemo(
    () => ({
      trackEvent,
      trackOnboardingStep,
      identifyUser,
      resetUser: resetMixpanelUser,
      getSignupSource,
    }),
    [identifyUser, trackEvent],
  );
};
